import React, {
  useEffect,
  forwardRef,
  useState,
  useMemo,
  useContext,
  useRef,
} from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Button,
  TextField,
  AlertTitle,
  InputLabel,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Grid,
  Typography,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useTheme } from "@mui/material/styles";
import MuiAlert from "@mui/material/Alert";
import { useTranslation } from "react-i18next";
import { fetchData, postData } from "../../utils/api";
import { UserContext } from "../../App";
import ExperimentProcessingStatus from "./ExperimentProcessingStatus";

const Alert = forwardRef((props, ref) => (
  <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

export default function ExperimentConfigurator() {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();
  // needed for form validation
  const formRef = useRef();

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // get type from URL query
  const experimentType = useQuery().get("type");

  const { datasets, currentDatasetIdx } = useContext(UserContext);
  const datasetName = datasets?.[currentDatasetIdx]?.datasetName;

  const [processingStarted, setProcessingStarted] = useState(false);
  const [experimentNames, setExperimentNames] = useState([]);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [runsFamilyData, setRunsFamilyData] = useState([]);
  const [childRunsNames, setChildRunsNames] = useState([]);
  const [selectedParentRun, setSelectedParentRun] = useState("");
  const [selectedRun, setSelectedRun] = useState(null);
  const [experimentName, setExperimentName] = useState("");
  const [isNameManual, setIsNameManual] = useState(false);
  // validation errors
  const [errors, setErrors] = useState({});
  const [trainedModelsExist, setTrainedModelsExist] = useState(null);
  const [isLoadingExperiments, setIsLoadingExperiments] = useState(false);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  // Error Snackbars
  const [snackbarSettings, setSnackbarSettings] = useState({
    isOpen: false,
    type: "info",
    message: "",
  });

  // Error Alert
  const [alertSettings, setAlertSettings] = useState({
    isVisible: false,
    type: "error",
    message: "",
  });

  const fetchExperiments = () => {
    // fetch Experiments to check if trained models exist
    if (!datasetName) {
      return;
    }
    setIsLoadingExperiments(true);
    setExperimentNames([]);
    setTrainedModelsExist(null);
    fetchData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiments`
    )
      .then((dataset) => {
        if (dataset.experiments.length !== 0) {
          dataset.experiments.forEach((experiment) => {
            const numTrainedModels = experiment.experimentProgress.trained;
            // if at least one trained model exists, show Error Model card
            if (numTrainedModels > 0) {
              // add experiment name to list of existing experiments
              setExperimentNames((prevNames) => [
                ...prevNames,
                experiment.experimentName,
              ]);
              setTrainedModelsExist(true);
            }
          });
        } else {
          setTrainedModelsExist(false);
        }
      })
      .finally(() => {
        setIsLoadingExperiments(false);
      });
  };

  // set base Data by API requests, when the current dataset is gathered
  useEffect(() => {
    if (datasets.length >= 1 && datasetName) {
      fetchExperiments();
    }
    setExperimentName(
      `Error Model${t("experimentTypeSelector.ExperimentNameEnd")}`
    );
    setIsNameManual(false);
    setSelectedExperiment(null);
    setRunsFamilyData([]);
    setChildRunsNames([]);
    setSelectedParentRun("");
    setSelectedRun(null);
  }, [datasets, currentDatasetIdx, datasetName, t]);

  const handleProcessSubmit = async () => {
    setAlertSettings({
      isVisible: false,
      type: "error",
      message: "",
    });
    if (!datasetName) {
      return;
    }
    const pipelineStepName = selectedRun
      ? selectedRun.replace(`${selectedParentRun}_`, "")
      : "";

    // set snackbar notification, dependent on post status
    const snackbar = { ...snackbarSettings };
    const data = await postData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/error-experiment`,
      {
        experimentName,
        originalExperimentID: selectedExperiment,
        originalRunID: selectedParentRun,
        originalPipelineStepName: pipelineStepName,
      }
    );

    // set snackbar notification, dependent on post status
    snackbar.message = data.data || data.message || "";
    if (data.status === 201) {
      setErrors({});
      snackbar.isOpen = true;
      snackbar.type = "success";
      snackbar.message = t(
        "experimentConfigurator.SuccessExperimentProcessingStarted"
      );
      // snackbar experiment list and set current experiment to just created
      fetchExperiments({ latest: experimentName });
      // then start processing: first rigger processing start via API
      const experimentData = await postData(
        `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiment/${experimentName}/processing`,
        {}
      );
      if (experimentData.status === 200) {
        // if processing was started, change website state to process-view
        setProcessingStarted(true);
      } else {
        snackbar.type = "error";
      }
    } else {
      snackbar.type = "error";
      if (data.status === 422) {
        // write error details to form
        setErrors(data.detail);
        setAlertSettings({
          isVisible: true,
          type: "error",
          message: t("experimentErrors.FailedCreationError"),
        });
      }
      if (data.status === 409) {
        // write error details to form
        setErrors(data.detail);

        snackbar.message = t(
          "experimentConfigurator.ErrorExperimentAlreadyExists"
        );
      }
    }

    setSnackbarSettings(snackbar);
  };

  useEffect(() => {}, [alertSettings]);

  const handleExperimentNameChange = (event) => {
    // reset error message
    if (errors.hasOwnProperty("experimentName")) {
      const errorsCopy = { ...errors };
      delete errorsCopy.experimentName;
      setErrors(errorsCopy);
    }
    setIsNameManual(true);
    setExperimentName(event.target.value);
  };

  const formatRunLabel = (run) => run.replace(/_(HPO\d+)/, "-$1");

  const updateExperimentName = (experiment, run, options = {}) => {
    const { force = false } = options;
    if (isNameManual && !force) {
      return;
    }
    if (experiment && run) {
      setExperimentName(`Error Model ${experiment} - ${formatRunLabel(run)}`);
      return;
    }
    if (experiment) {
      setExperimentName(`Error Model ${experiment}`);
      return;
    }
    if (force) {
      setExperimentName(
        `Error Model${t("experimentTypeSelector.ExperimentNameEnd")}`
      );
    }
  };

  const fetchRunsForExperiment = (experiment) => {
    if (!datasetName || !experiment) {
      return;
    }

    setIsLoadingRuns(true);
    setRunsFamilyData([]);
    setChildRunsNames([]);
    setSelectedParentRun("");
    setSelectedRun(null);

    fetchData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiment/${experiment}/runs`
    )
      .then((data) => {
        setRunsFamilyData(data.runs);
        if (data.runs.length === 1) {
          const runFamily = data.runs.at(0);
          const children = runFamily.children.map((child) => child.runName);
          setSelectedParentRun(runFamily.parent.runName);
          setChildRunsNames(children);
          const lastChildRun = runFamily.children.at(
            runFamily.children.length - 1
          );
          setSelectedRun(lastChildRun.runName);
          updateExperimentName(experiment, lastChildRun.runName);
        }
      })
      .finally(() => {
        setIsLoadingRuns(false);
      });
  };

  const handleExperimentChange = (event) => {
    const experiment = event.target.value;
    if (experiment === "None") {
      setSelectedExperiment(null);
      setRunsFamilyData([]);
      setChildRunsNames([]);
      setSelectedParentRun("");
      setSelectedRun(null);
      setIsLoadingRuns(false);
      setIsNameManual(false);
      updateExperimentName(null, null, { force: true });
      return;
    }

    setSelectedExperiment(experiment);
    updateExperimentName(experiment, null);
  };

  const handleParentRunChange = (event) => {
    const parentRun = event.target.value;
    const runFamilyData = runsFamilyData.find(
      (data) => data.parent.runName === parentRun
    );
    if (!runFamilyData) {
      setSelectedParentRun(parentRun);
      setChildRunsNames([]);
      setSelectedRun(null);
      updateExperimentName(selectedExperiment, null);
      return;
    }

    setSelectedParentRun(parentRun);
    const children = runFamilyData.children.map((child) => child.runName);
    setChildRunsNames(children);
    const lastChildRun = runFamilyData.children.at(
      runFamilyData.children.length - 1
    );
    setSelectedRun(lastChildRun.runName);

    updateExperimentName(selectedExperiment, lastChildRun.runName);
  };

  const handleRunChange = (event) => {
    const runName = event.target.value;
    setSelectedRun(runName);
    updateExperimentName(selectedExperiment, runName);
  };

  useEffect(() => {
    if (!selectedExperiment) {
      return;
    }
    fetchRunsForExperiment(selectedExperiment);
  }, [selectedExperiment, datasetName]);

  useEffect(() => {
    console.log("selectedRun", selectedRun);
  }, [selectedRun]);
  const shouldShowLoader = datasets.length >= 1 && isLoadingExperiments;
  const isFormValid = Boolean(
    datasetName &&
      selectedExperiment &&
      selectedParentRun &&
      selectedRun &&
      experimentName.trim()
  );

  let content = null;

  if (processingStarted) {
    // if processing was already initialized,show status
    content = <ExperimentProcessingStatus experimentName={experimentName} />;
  } else if (experimentNames.length >= 1 && trainedModelsExist) {
    // else render experiment configuration
    content = (
      <form ref={formRef}>
        <Grid
          id="experiment-configurator-container"
          container
          spacing={2}
          sx={{ padding: theme.spacing(4, 1) }}
        >
          <Grid item lg={6} xs={12}>
            <Typography variant="h4">
              {t("experimentConfigurator.PageTitle_1")} {experimentType}{" "}
              {t("experimentConfigurator.PageTitle_2")}
              <em>{datasetName || ""}</em>
            </Typography>
            <br />
            <Grid container rowSpacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {t("experimentConfigurator.ExperimentSettingsTitle")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl required error={Boolean(errors.target)}>
                  <InputLabel>
                    {t("currentExperimentSelector.CurrentExperimentDropdown")}
                  </InputLabel>
                  <Select
                    labelId="select-experiment"
                    // set None as default
                    value={selectedExperiment || "None"}
                    onChange={handleExperimentChange}
                    sx={{ minWidth: 200 }}
                    required
                  >
                    <MenuItem value="None">
                      <em>{t("experimentConfigurator.ExperimentSelection")}</em>
                    </MenuItem>
                    {experimentNames.map((name, index) => (
                      <MenuItem
                        key={`experiment-selection-${index}`}
                        value={name}
                      >
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {t(
                      `experimentConfigurator.Error ${errors.target?.message}`
                    )}
                  </FormHelperText>
                </FormControl>
                <Tooltip title={t("experimentConfigurator.HintExperiment")}>
                  <IconButton>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <FormControl required error={Boolean(errors.target)}>
                  <InputLabel>{t("analyzer.SelectedRunDropdown")}</InputLabel>
                  <Select
                    labelId="select-experiment"
                    // set None as default
                    value={selectedParentRun || "None"}
                    onChange={handleParentRunChange}
                    sx={{ minWidth: 200 }}
                    disabled={!selectedExperiment || isLoadingRuns}
                    required
                  >
                    <MenuItem value="None">
                      <em>{t("experimentConfigurator.RunSelection")}</em>
                    </MenuItem>
                    {runsFamilyData.map((data, index) => (
                      <MenuItem
                        key={`run-selection-${index}`}
                        value={data.parent.runName}
                      >
                        {data.parent.runName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title={t("experimentConfigurator.HintRun")}>
                  <IconButton>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              {childRunsNames.length > 0 && (
                <Grid item xs={12}>
                  <FormControl required error={Boolean(errors.target)}>
                    <InputLabel>
                      {t("analyzer.SelectedPipelineStepDropdown")}
                    </InputLabel>
                    <Select
                      labelId="select-pipeline-step"
                      // set None as default
                      value={selectedRun || "None"}
                      onChange={handleRunChange}
                      sx={{ minWidth: 200 }}
                      disabled={!selectedExperiment || isLoadingRuns}
                      required
                    >
                      <MenuItem value="None">
                        <em>{t("experimentConfigurator.RunSelection")}</em>
                      </MenuItem>
                      {childRunsNames.map((name, index) => (
                        <MenuItem
                          key={`pipeline-selection-${index}`}
                          value={name}
                        >
                          {name.replace(`${selectedParentRun}_`, "")}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl>
                  <TextField
                    label={t("experimentConfigurator.ExperimentNameFillable")}
                    value={`${experimentName}`}
                    onChange={handleExperimentNameChange}
                    error={Boolean(errors.experimentName)}
                    required
                    helperText={t(
                      `experimentConfigurator.Error ${errors.experimentName?.message}`
                    )}
                  />
                </FormControl>
                <Tooltip title={t("experimentConfigurator.HintExperimentName")}>
                  <IconButton>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>

            {alertSettings.isVisible && (
              <Alert severity={alertSettings.type} sx={{ marginTop: "10px" }}>
                <AlertTitle>{alertSettings.message}</AlertTitle>
                {errors && (
                  <div>
                    {Object.keys(errors).map((error) => (
                      <div key={error}>
                        {" "}
                        - {t(`experimentErrors.${error}`, errors[error])}
                      </div>
                    ))}
                  </div>
                )}
              </Alert>
            )}
          </Grid>
          {/* LEFT SIDE */}
          <Grid item lg={9} xs={6} />
          <Grid item lg={3} xs={6} sx={{ alignSelf: "end" }}>
            <Button
              color="primary"
              size="medium"
              variant="contained"
              disabled={!isFormValid}
              onClick={() => {
                if (formRef.current.reportValidity()) {
                  // check required fields
                  handleProcessSubmit();
                }
              }}
            >
              {t("experimentConfigurator.SaveAndProcessButton")}
            </Button>
          </Grid>
        </Grid>
      </form>
    );
  } else {
    content = (
      <Typography variant="h4">
        {t("experimentConfigurator.TrainedModelRequired")}{" "}
        <em>
          <Link to="/quality-check">Experiment</Link>
        </em>
      </Typography>
    );
  }

  return datasets.length >= 1 ? (
    <>
      <Snackbar open={snackbarSettings.isOpen} autoHideDuration={8000}>
        <Alert severity={snackbarSettings.type}>
          {snackbarSettings.message}
        </Alert>
      </Snackbar>
      {shouldShowLoader ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: "40vh" }}
        >
          <CircularProgress />
        </Grid>
      ) : (
        content
      )}
    </>
  ) : (
    // if user lands on this page, without having at least one dataset, show this hint
    <Grid item xs={3} sx={{ margin: theme.spacing(6) }}>
      <Typography variant="body">
        {t("experimentConfigurator.HintNoDatasetYet")}
      </Typography>
    </Grid>
  );
}
